/**
 * useBulkInvoiceShipments - Hook para prefacturar múltiples shipments en bloque
 * 
 * Permite prefacturar varios shipments simultáneamente mediante POST a /invoices/pre-invoices
 * con el body { shipment_ids: [...] }. Muestra toasts de éxito/error, limpia la selección
 * y ejecuta refetch de datos al completar exitosamente.
 * 
 * @returns {Object} { bulkInvoiceShipments: función de prefacturación, isInvoicing: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { ShipmentsApiAdapter } from '../adapters/ShipmentsApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';
export const useBulkInvoiceShipments = () => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { toast } = useToast();
  const [isInvoicing, setIsInvoicing] = useState(false);

  const bulkInvoiceShipments = useCallback(
    async (shipmentIds: number[], onSuccess?: () => void) => {
      setIsInvoicing(true);

      try {
        const shipmentsAdapter = new ShipmentsApiAdapter(
          createHttpClient(apiClientWH)
        );
        await shipmentsAdapter.bulkInvoiceShipments(shipmentIds);

        // 🎯 Si llegamos aquí, la API devolvió 200 OK = éxito
        toast({
          title: t('toast.bulk_invoice_success.title', 'Shipments prefacturados'),
          description: t('toast.bulk_invoice_success.description', {
            count: shipmentIds.length,
            defaultValue: `Se han prefacturado ${shipmentIds.length} shipments exitosamente`,
          }),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch de datos)
        onSuccess?.();
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;

        let description = apiMessage;

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.bulk_invoice_error.unexpected',
              'Error inesperado al prefacturar los shipments. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.bulk_invoice_error.title', 'Error al prefacturar'),
          description,
          variant: 'destructive',
        });
      } finally {
        setIsInvoicing(false);
      }
    },
    [t, toast]
  );

  return {
    bulkInvoiceShipments,
    isInvoicing,
  };
};

